import React, { useState, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Message, AuthContext } from '../lib';
import { useSocket } from '../../app/socketContext';
import { SYNC_STAGE_CONFIG, SYNC_STAGES } from '../../constants/syncStages';
import { getAppDisplayName } from '../../utils/appUtils';
import styles from './ConnectionSummary.module.scss';

export function ConnectionSummary({ connectionSummary, connSummaryTitle, connSummary }) {
  const authContext = useContext(AuthContext);
  const { syncState } = useSocket();
  const [showModal, setShowModal] = useState(false);

  // Check if user has an active app connection
  const hasActiveApp = authContext.user?.app_connection_id;

  const messageActions = (
    <div className={styles.messageActions}>
      {hasActiveApp && (
        <>
          {/* <button 
            className={styles.refreshButton}
            onClick={triggerManualSync}
            title="Refresh data"
          >
            🔄
          </button> */}
          {syncState.history.length > 0 && (
            <button 
              className={styles.historyButton}
              onClick={() => setShowModal(true)}
              title="View sync history"
            >
              History
            </button>
          )}
        </>
      )}
    </div>
  );

  return (
    <>
      {connectionSummary.loading === false && (
        <Message
          title={connSummaryTitle}
          text={connSummary != null ? ('Last Data Seen At: ' + connSummary?.last_updated_at) : ''}
          type={connSummary != null ? 'success' : 'warning'}
          actions={messageActions}
        />
      )}

      {/* Modal for sync history - use same modal as SyncToast */}
      {showModal && (
        <SyncModalPortal 
          onClose={() => setShowModal(false)}
          syncStatus={syncState.status}
          currentMessage={syncState.currentMessage}
          appName={syncState.appName}
          progress={syncState.progress}
          syncHistory={syncState.history}
          currentStage={syncState.currentStage}
        />
      )}
    </>
  );
}

// Portal wrapper for the modal
function SyncModalPortal(props) {
  return createPortal(
    <SyncModal {...props} />,
    document.body
  );
}

// Modal component (shared with SyncToast)
function SyncModal({ onClose, syncStatus, currentMessage, appName, progress, syncHistory, currentStage }) {
  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);
  
  // Get display name for the app
  const displayAppName = getAppDisplayName(appName);
  
  const syncStages = Object.entries(SYNC_STAGE_CONFIG).map(([id, config]) => ({
    id,
    ...config
  })).filter(stage => stage.id !== SYNC_STAGES.ERROR); // Don't show error stage in progress flow

  function getStageStatus(stage) {
    if (!currentStage) return 'pending';
    
    const currentIndex = syncStages.findIndex(s => s.id === currentStage);
    const stageIndex = syncStages.findIndex(s => s.id === stage.id);
    
    if (stageIndex < currentIndex) return 'completed';
    if (stageIndex === currentIndex) return 'active';
    return 'pending';
  }

  function getStageIcon(stage) {
    const status = getStageStatus(stage);
    if (status === 'completed') return '✓';
    if (status === 'active') return '⏳';
    return stage.icon;
  }

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{displayAppName && `${displayAppName} `}Data Sync Progress</h3>
          <button onClick={onClose} className={styles.closeButton}>×</button>
        </div>
        
        <div className={styles.modalContent}>
          {syncStatus !== 'idle' && (
            <div className={styles.stagesContainer}>
              {syncStages.map(stage => (
                <div 
                  key={stage.id} 
                  className={`${styles.stage} ${styles[getStageStatus(stage)]}`}
                >
                  <div className={styles.stageIcon}>
                    <span>{getStageIcon(stage)}</span>
                  </div>
                  <div className={styles.stageContent}>
                    <h4>{stage.title}</h4>
                  </div>
                </div>
              ))}
            </div>
          )}

          {syncHistory.length > 0 && (
            <div className={styles.historySection}>
              <h4>Recent Activity</h4>
              <div className={styles.historyList}>
                {syncHistory.slice(-15).reverse().map(item => (
                  <div 
                    key={item.id} 
                    className={`${styles.historyItem} ${item.error ? styles.error : ''}`}
                  >
                    <span className={styles.timestamp}>
                      {item.timestamp instanceof Date ? item.timestamp.toLocaleTimeString() : new Date(item.timestamp || Date.now()).toLocaleTimeString()}
                    </span>
                    <span className={styles.message}>
                      {item.message}
                    </span>
                    {item.record_count && (
                      <span className={styles.detail}>
                        {item.record_count} records
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {syncHistory.length === 0 && (
            <div className={styles.noHistory}>
              <p>No sync history available yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}